import type { Schema, SuccessResponse } from "../types/api-helpers.d.ts";
import { $, bootstrap } from "../lib/globals.js";
import { APP_CONFIG } from "../lib/config.js";
import { emit } from "../lib/metrics-client.js";
import { clearOpenForm } from "../lib/modal-tracking.js";
import { UI_EVENTS } from "../types/metrics-events.js";
import { showNewPageOnAJAXHTMLResponse } from "../lib/page-utils.js";
import {
  showSplashModalAlertBanner,
  resetModalFormState,
  handleImproperFormErrors,
  handleUserHasAccountNotEmailValidated,
  switchModal,
} from "./init.js";
import { VALIDATION_FORM } from "../types/metrics-dim-values.js";

type LoginRequest = Schema<"LoginRequest">;
type LoginSuccess = SuccessResponse<"login">;
type LoginError = Schema<"ErrorResponse_LoginErrorCodes">;

/**
 * Initialize login form handlers
 * Must be called after login form HTML is loaded into the modal
 */
export function initLoginForm($modal: JQuery): void {
  $modal
    .find("#ToRegisterFromLogin")
    .offAndOn("click", () => switchModal($modal, "#RegisterModal"));

  $modal
    .find(".to-forgot-password")
    .offAndOn("click", () => switchModal($modal, "#ForgotPasswordModal"));

  $modal
    .find("#submit")
    .offAndOn("click", (event) => handleLogin(event, $modal));

  $modal.on("show.bs.modal", () => resetModalFormState($modal));
}

function handleLogin(event: JQuery.TriggeredEvent, $modal: JQuery): void {
  event.preventDefault();
  emit({ event: UI_EVENTS.UI_LOGIN_SUBMIT });
  clearOpenForm();
  $modal.find("#submit").attr("disabled", "disabled").attr("aria-busy", "true");

  // Allow user to attach a query param `next` if browser URL currently includes it
  // This allows for User to be given a link to a UTubID but they haven't logged in recently
  let url: string = APP_CONFIG.routes.login;
  const searchParams = new URLSearchParams(window.location.search);
  const nextQueryParam = searchParams.get("next");
  if (searchParams.size === 1 && nextQueryParam !== null) {
    url = `${url}?${searchParams.toString()}`;
  }

  const username: string = String($modal.find("#username").val() ?? "");
  const password: string = String($modal.find("#password").val() ?? "");

  const payload: LoginRequest = { username, password };
  const loginRequest: JQuery.jqXHR = $.ajax({
    url: url,
    type: "POST",
    data: JSON.stringify(payload),
    contentType: "application/json",
  });

  loginRequest.done((response, textStatus, xhr) =>
    handleLoginSuccess(response, textStatus, xhr),
  );
  loginRequest.fail((xhr, textStatus, error) =>
    handleLoginFailure(xhr, textStatus, error, $modal),
  );
}

function handleLoginSuccess(
  response: LoginSuccess,
  _: string,
  xhr: JQuery.jqXHR,
): void {
  if (xhr.status === 200) {
    bootstrap.Modal.getOrCreateInstance("#LoginModal").hide();
    // Use redirectUrl from JSON response
    const redirectUrl = response.redirectUrl || APP_CONFIG.routes.home;
    window.location.replace(redirectUrl);
  }
}

function handleLoginFailure(
  xhr: JQuery.jqXHR,
  _: string,
  error: string,
  $modal: JQuery,
): void {
  if (!("responseJSON" in xhr)) {
    if (xhr.getResponseHeader("Content-Type") === "text/html; charset=utf-8") {
      switch (xhr.status) {
        case 403:
        case 429: {
          showNewPageOnAJAXHTMLResponse(xhr.responseText);
          return;
        }
      }
    }
    window.location.assign(APP_CONFIG.routes.errorPage);
    return;
  }

  if (
    (xhr.status === 400 || xhr.status === 401) &&
    "errorCode" in xhr.responseJSON
  ) {
    const errorJson = xhr.responseJSON as LoginError;
    emit({ event: UI_EVENTS.UI_VALIDATION_ERROR, form: VALIDATION_FORM.LOGIN });
    switch (errorJson.errorCode) {
      case 1: {
        // User found but email not yet validated
        handleUserHasAccountNotEmailValidated($modal, errorJson.message);
        $modal.find("input").attr("disabled", "disabled");
        break;
      }
      case 2: {
        handleImproperFormErrors($modal, errorJson);
        $modal.find("#submit").removeAttr("disabled").removeAttr("aria-busy");
        break;
      }
    }
  } else {
    showSplashModalAlertBanner(
      $modal,
      "Unable to process request...",
      "danger",
    );
    $modal.find("#submit").removeAttr("disabled").removeAttr("aria-busy");
  }
}
